/**
 * Composes multiple wrapper functions (HOCs) into a single wrapper
 * Wrappers are applied from right to left (like function composition)
 *
 * @param {Array<Function>} wrappers - Array of wrapper functions
 * @returns {Function} - Composed wrapper function
 *
 * Example:
 * const wrappedRender = composeWrappers([
 *   withFormRenderWrapper(app),
 *   withDetailsBreadcrumbsWrapper(),
 *   withSettingsTabsWrapper(app)
 * ]);
 */
export const composeWrappers = (wrappers) => {
    if (!Array.isArray(wrappers) || wrappers.length === 0) {
        throw new Error('composeWrappers expects a non-empty array of wrapper functions');
    }

    // Apply wrappers from right to left (standard composition order)
    return (Component) => {
        return wrappers.reduceRight((WrappedComponent, wrapper) => {
            if (typeof wrapper !== 'function') {
                throw new Error('All items in wrappers array must be functions');
            }
            return wrapper(WrappedComponent);
        }, Component);
    };
};

/**
 * Alternative helper function that creates a render function with composed wrappers
 * This is more convenient for direct use in route definitions
 *
 * @param {Array<Function>} wrappers - Array of wrapper functions
 * @returns {Function} - Render function that applies all wrappers
 */
export const createRenderWithWrappers = (wrappers) => {
    const composedWrapper = composeWrappers(wrappers);
    return composedWrapper;
};
